﻿package br.com.htms.inspeccaocampo.export

import br.com.htms.inspeccaocampo.data.AgregacaoInspecao
import br.com.htms.inspeccaocampo.data.AgregacaoRespostas
import br.com.htms.inspeccaocampo.data.FormTipo
import br.com.htms.inspeccaocampo.data.InspecaoCompleta
import br.com.htms.inspeccaocampo.data.RespostaConformidade
import br.com.htms.inspeccaocampo.data.RespostaTernaria
import br.com.htms.inspeccaocampo.platform.formatDateBr
import br.com.htms.inspeccaocampo.platform.formatDateTimeBr

/** Inspeção com metadados para o consolidado completo. */
data class ConsolidadoInspecao(
    val username: String,
    val remoteId: String?,
    val inspecao: InspecaoCompleta
)

/**
 * Workbook multi-aba: Resumo | Respostas | Acoes | Dashboard.
 * Sem fotos/assinaturas (arquivo gerencial textual).
 * Dashboard usa fórmulas sobre Resumo + gráficos OOXML (pizza/barras).
 */
object SharedExcelConsolidado {

    private const val STYLE_BODY = SharedOoxmlWriter.STYLE_BODY
    private const val STYLE_HEADER = SharedOoxmlWriter.STYLE_HEADER
    private const val STYLE_TITLE = SharedOoxmlWriter.STYLE_TITLE
    private const val STYLE_KPI_LABEL = SharedOoxmlWriter.STYLE_KPI_LABEL
    private const val STYLE_KPI_VALUE = SharedOoxmlWriter.STYLE_KPI_VALUE
    private const val STYLE_SECTION = SharedOoxmlWriter.STYLE_SECTION
    private const val STYLE_CRIT_ALTA = SharedOoxmlWriter.STYLE_CRIT_ALTA
    private const val STYLE_CRIT_MEDIA = SharedOoxmlWriter.STYLE_CRIT_MEDIA
    private const val STYLE_CRIT_BAIXA = SharedOoxmlWriter.STYLE_CRIT_BAIXA

    fun build(items: List<ConsolidadoInspecao>): ByteArray {
        val mapaResp = AgregacaoInspecao.mapaResponsaveis(
            items.flatMap { listOf(it.inspecao.responsavelResumo(), it.username) }
        )
        val resumo = buildResumo(items, mapaResp)
        val respostas = buildRespostas(items, mapaResp)
        val acoes = buildAcoes(items, mapaResp)
        val dashboard = buildDashboard(items, mapaResp)
        return SharedOoxmlWriter.write(
            sheets = listOf(resumo, respostas, acoes, dashboard),
            media = emptyMap()
        )
    }

    private fun obraOf(insp: InspecaoCompleta): String =
        AgregacaoInspecao.obra(insp.obraResumo(), "(sem obra)")

    private fun responsavelOf(item: ConsolidadoInspecao, mapa: Map<String, String>): String {
        val bruto = item.inspecao.responsavelResumo().ifBlank { item.username }
        return AgregacaoInspecao.responsavel(bruto, mapa)
    }

    /**
     * Pacote com 4 abas + gráficos válidos para validar OOXML.
     */
    fun buildStructureSmokeBytes(): ByteArray {
        val pieFixed = SharedExcelChart(
            type = SharedExcelChartType.PIE,
            title = "Respostas",
            fromCol = 0,
            fromRow = 12,
            toCol = 5,
            toRow = 26,
            catRange = "\$A\$8:\$A\$10",
            valRange = "\$B\$8:\$B\$10",
            categories = listOf("C", "NC", "NA"),
            values = listOf(3.0, 1.0, 1.0)
        )
        return SharedOoxmlWriter.write(
            sheets = listOf(
                SharedExcelSheet(
                    name = "Resumo",
                    rows = listOf(
                        SharedExcelRow(1, listOf(SharedExcelCell(0, "Titulo", STYLE_TITLE))),
                        SharedExcelRow(3, listOf(SharedExcelCell(0, "Data/Hora", STYLE_HEADER))),
                        SharedExcelRow(
                            4,
                            listOf(
                                SharedExcelCell(0, "01/01/2026"),
                                SharedExcelCell(7, "2", numeric = true),
                                SharedExcelCell(8, "1", numeric = true),
                                SharedExcelCell(9, "0", numeric = true),
                                SharedExcelCell(10, "MEDIA", STYLE_CRIT_MEDIA)
                            )
                        )
                    ),
                    merges = listOf("A1:N1"),
                    freezeRows = 3,
                    autoFilter = "A3:N4",
                    tabColor = "1565C0",
                    colWidths = mapOf(0 to 16)
                ),
                SharedExcelSheet(
                    name = "Respostas",
                    rows = listOf(SharedExcelRow(1, listOf(SharedExcelCell(0, "Respostas", STYLE_TITLE)))),
                    merges = listOf("A1:J1"),
                    freezeRows = 3,
                    tabColor = "26A69A"
                ),
                SharedExcelSheet(
                    name = "Acoes",
                    rows = listOf(SharedExcelRow(1, listOf(SharedExcelCell(0, "Acoes", STYLE_TITLE)))),
                    merges = listOf("A1:G1"),
                    freezeRows = 3,
                    tabColor = "FFB74D"
                ),
                SharedExcelSheet(
                    name = "Dashboard",
                    rows = listOf(
                        SharedExcelRow(1, listOf(SharedExcelCell(0, "Dashboard", STYLE_TITLE))),
                        SharedExcelRow(
                            8,
                            listOf(
                                SharedExcelCell(0, "C / Sim", STYLE_BODY),
                                SharedExcelCell(
                                    1,
                                    "2",
                                    numeric = true,
                                    style = STYLE_KPI_VALUE,
                                    formula = "SUM(Resumo!G4:G4)"
                                )
                            )
                        ),
                        SharedExcelRow(
                            9,
                            listOf(
                                SharedExcelCell(0, "NC / Nao"),
                                SharedExcelCell(1, "1", numeric = true, formula = "SUM(Resumo!H4:H4)")
                            )
                        ),
                        SharedExcelRow(
                            10,
                            listOf(
                                SharedExcelCell(0, "NA / N/A"),
                                SharedExcelCell(1, "0", numeric = true, formula = "SUM(Resumo!I4:I4)")
                            )
                        )
                    ),
                    merges = listOf("A1:H1"),
                    freezeRows = 2,
                    charts = listOf(pieFixed),
                    tabColor = "0D47A1",
                    colWidths = mapOf(0 to 28, 1 to 10)
                )
            ),
            media = emptyMap()
        )
    }

    private fun buildResumo(items: List<ConsolidadoInspecao>, mapaResp: Map<String, String>): SharedExcelSheet {
        val rows = mutableListOf<SharedExcelRow>()
        var r = 1
        rows += SharedExcelRow(
            r++,
            listOf(SharedExcelCell(0, "Relatorio consolidado - Resumo", STYLE_TITLE))
        )
        rows += SharedExcelRow(
            r++,
            listOf(
                SharedExcelCell(
                    0,
                    "${items.size} inspecao(oes) | obras e responsaveis unidos | filtre pelas setas — Dashboard acompanha"
                )
            )
        )
        val headerRow = r
        rows += SharedExcelRow(
            r++,
            listOf(
                SharedExcelCell(0, "Data/Hora", STYLE_HEADER),
                SharedExcelCell(1, "Usuario", STYLE_HEADER),
                SharedExcelCell(2, "Checklist", STYLE_HEADER),
                SharedExcelCell(3, "Obra", STYLE_HEADER),
                SharedExcelCell(4, "Responsavel", STYLE_HEADER),
                SharedExcelCell(5, "Efetivo", STYLE_HEADER),
                SharedExcelCell(6, "C", STYLE_HEADER),
                SharedExcelCell(7, "NC", STYLE_HEADER),
                SharedExcelCell(8, "NA", STYLE_HEADER),
                SharedExcelCell(9, "Criticidade", STYLE_HEADER),
                SharedExcelCell(10, "Origem", STYLE_HEADER),
                SharedExcelCell(11, "ID", STYLE_HEADER),
                SharedExcelCell(12, "ID remoto (completo)", STYLE_HEADER)
            )
        )
        items.forEach { item ->
            val insp = item.inspecao
            val counts = AgregacaoRespostas.of(insp)
            val efetivo = efetivoOf(insp)
            val efetivoNum = efetivo.filter { it.isDigit() || it == '.' || it == ',' }
                .replace(',', '.')
            rows += SharedExcelRow(
                r++,
                listOf(
                    SharedExcelCell(
                        0,
                        formatDateTimeBr(insp.dataHorarioMsResumo().takeIf { it > 0 } ?: insp.criadoEm)
                    ),
                    SharedExcelCell(1, item.username),
                    SharedExcelCell(2, insp.formTipo.rotulo),
                    SharedExcelCell(3, obraOf(insp)),
                    SharedExcelCell(4, responsavelOf(item, mapaResp)),
                    if (efetivoNum.toDoubleOrNull() != null) {
                        SharedExcelCell(5, efetivoNum, numeric = true)
                    } else {
                        SharedExcelCell(5, efetivo)
                    },
                    SharedExcelCell(6, counts.c.toString(), numeric = true, style = STYLE_BODY),
                    SharedExcelCell(7, counts.nc.toString(), numeric = true, style = STYLE_BODY),
                    SharedExcelCell(8, counts.na.toString(), numeric = true, style = STYLE_BODY),
                    SharedExcelCell(9, AgregacaoRespostas.criticidade(counts), style = critStyle(AgregacaoRespostas.criticidade(counts))),
                    SharedExcelCell(10, if (item.remoteId != null) "Remoto" else "Local", style = STYLE_BODY),
                    SharedExcelCell(11, shortId(item), style = STYLE_BODY),
                    SharedExcelCell(12, item.remoteId.orEmpty(), style = STYLE_BODY)
                )
            )
        }
        val lastDataRow = (r - 1).coerceAtLeast(headerRow)
        return SharedExcelSheet(
            name = "Resumo",
            rows = rows,
            merges = listOf("A1:M1", "A2:M2"),
            freezeRows = headerRow,
            autoFilter = "A$headerRow:M$lastDataRow",
            tabColor = "1565C0",
            colWidths = mapOf(
                0 to 16, 1 to 14, 2 to 26, 3 to 36, 4 to 28, 5 to 10,
                6 to 6, 7 to 6, 8 to 6, 9 to 12, 10 to 10, 11 to 12, 12 to 20
            )
        )
    }

    @Suppress("UNUSED_PARAMETER")
    private fun buildRespostas(items: List<ConsolidadoInspecao>, mapaResp: Map<String, String>): SharedExcelSheet {
        val rows = mutableListOf<SharedExcelRow>()
        var r = 1
        rows += SharedExcelRow(
            r++,
            listOf(SharedExcelCell(0, "Respostas detalhadas - 1 linha por pergunta", STYLE_TITLE))
        )
        r++
        val headerRow = r
        rows += SharedExcelRow(
            r++,
            listOf(
                SharedExcelCell(0, "Data/Hora", STYLE_HEADER),
                SharedExcelCell(1, "Usuario", STYLE_HEADER),
                SharedExcelCell(2, "Checklist", STYLE_HEADER),
                SharedExcelCell(3, "Obra", STYLE_HEADER),
                SharedExcelCell(4, "Secao", STYLE_HEADER),
                SharedExcelCell(5, "Item", STYLE_HEADER),
                SharedExcelCell(6, "Resposta", STYLE_HEADER),
                SharedExcelCell(7, "Justificativa / detalhe", STYLE_HEADER),
                SharedExcelCell(8, "Valor extra", STYLE_HEADER),
                SharedExcelCell(9, "ID", STYLE_HEADER)
            )
        )
        items.forEach { item ->
            val insp = item.inspecao
            val whenStr = formatDateTimeBr(insp.dataHorarioMsResumo().takeIf { it > 0 } ?: insp.criadoEm)
            val id = shortId(item)
            val base = listOf(whenStr, item.username, insp.formTipo.rotuloCurto, obraOf(insp))
            when (insp.formTipo) {
                FormTipo.INSPECAO_CAMPO -> {
                    insp.form.itens.forEach { it ->
                        rows += SharedExcelRow(
                            r++,
                            cellsOf(
                                base + listOf(
                                    "",
                                    it.titulo,
                                    it.resposta?.rotulo.orEmpty(),
                                    it.justificativa,
                                    "",
                                    id
                                )
                            )
                        )
                    }
                }
                FormTipo.RELATORIO_FOTO_ALOJAMENTO,
                FormTipo.RELATORIO_ALOJAMENTO -> {
                    insp.relatorioFoto?.itens?.forEach { it ->
                        rows += SharedExcelRow(
                            r++,
                            cellsOf(
                                base + listOf(
                                    it.secao,
                                    it.tituloNumerado,
                                    it.resposta?.rotuloSimNao.orEmpty(),
                                    it.justificativa,
                                    listOfNotNull(
                                        it.labelExtra.takeIf { l -> l.isNotBlank() }?.let { l -> "$l: ${it.valorExtra}" }
                                            ?: it.valorExtra.takeIf { v -> v.isNotBlank() }
                                    ).joinToString(" "),
                                    id
                                )
                            )
                        )
                    }
                    if (insp.formTipo == FormTipo.RELATORIO_ALOJAMENTO) {
                        insp.checklistAlojamento?.itens?.forEach { it ->
                            rows += SharedExcelRow(
                                r++,
                                cellsOf(
                                    base + listOf(
                                        "Parte2 - ${it.secao}",
                                        it.tituloNumerado,
                                        it.resposta?.codigo.orEmpty(),
                                        it.justificativa,
                                        "",
                                        id
                                    )
                                )
                            )
                        }
                    }
                }
                FormTipo.CHECKLIST_ALOJAMENTO,
                FormTipo.CHECKLIST_VETORES -> {
                    insp.checklistAlojamento?.itens?.forEach { it ->
                        rows += SharedExcelRow(
                            r++,
                            cellsOf(
                                base + listOf(
                                    it.secao,
                                    it.tituloNumerado,
                                    it.resposta?.codigo.orEmpty(),
                                    it.justificativa,
                                    "",
                                    id
                                )
                            )
                        )
                    }
                }
                FormTipo.CHECKLIST_DIARIO_HTMS,
                FormTipo.CHECKLIST_DIARIO_AXIA -> {
                    insp.checklistDiario?.itens?.forEach { it ->
                        rows += SharedExcelRow(
                            r++,
                            cellsOf(
                                base + listOf(
                                    it.secao,
                                    it.tituloNumerado,
                                    it.resposta?.codigo.orEmpty(),
                                    it.justificativa,
                                    "",
                                    id
                                )
                            )
                        )
                    }
                }
                FormTipo.COMUNICACAO_OCORRENCIAS -> {
                    val o = insp.comunicacaoOcorrencias
                    if (o != null) {
                        fun row(campo: String, valor: String) {
                            if (valor.isBlank()) return
                            rows += SharedExcelRow(
                                r++,
                                cellsOf(
                                    base + listOf(
                                        "Ocorrencia",
                                        campo,
                                        valor,
                                        "",
                                        "",
                                        id
                                    )
                                )
                            )
                        }
                        row("Cliente", o.cliente)
                        row("Caracterizacao", o.caracterizacao?.rotulo.orEmpty())
                        row("Tipo", o.tipo?.rotulo.orEmpty())
                        row("Data/hora ocorrido", o.dataHoraOcorrido)
                        row("Local exato", o.localExato)
                        row("Quem reportando", o.quemReportando)
                        row("Tipo de evento", o.tipoEvento?.rotulo.orEmpty())
                        row("Envolvido nome", o.envolvidoNome)
                        row("Envolvido funcao", o.envolvidoFuncao)
                        row("Responsavel atividade", o.responsavelAtividade)
                        row("Cargo", o.responsavelCargo)
                        row("Estava no local", o.responsavelEstavaNoLocal?.rotulo.orEmpty())
                        row("Data acidente", o.dataAcidente)
                        row("Hora acidente", o.horaAcidente)
                        row("Horas trabalhadas", o.horasTrabalhadas)
                        row("Local acidente", o.localAcidente)
                        row("Parte do corpo", o.parteCorpoLesionada)
                        row("Agente causador", o.agenteCausador)
                        row("Natureza lesao", o.naturezaLesao)
                        row("Acoes imediatas", o.acoesTomadasImediato)
                        row("Descricao evento", o.descricaoEvento)
                    }
                }
                FormTipo.REM_SEGURANCA_OBRAS,
            FormTipo.RELATORIO_SEGURANCA,
            FormTipo.ACOMPANHAMENTO_AMBIENTAL,
            FormTipo.EVIDENCIA_TREINAMENTO,
            FormTipo.ANALISE_VISUAL_NR -> Unit
            }
        }
        val lastDataRow = (r - 1).coerceAtLeast(headerRow)
        return SharedExcelSheet(
            name = "Respostas",
            rows = rows,
            merges = listOf("A1:J1"),
            freezeRows = headerRow,
            autoFilter = "A$headerRow:J$lastDataRow",
            tabColor = "26A69A",
            colWidths = mapOf(
                0 to 16, 1 to 14, 2 to 14, 3 to 24, 4 to 20, 5 to 40, 6 to 14, 7 to 36, 8 to 16, 9 to 12
            )
        )
    }

    @Suppress("UNUSED_PARAMETER")
    private fun buildAcoes(items: List<ConsolidadoInspecao>, mapaResp: Map<String, String>): SharedExcelSheet {
        val rows = mutableListOf<SharedExcelRow>()
        var r = 1
        rows += SharedExcelRow(
            r++,
            listOf(SharedExcelCell(0, "Acoes, desvios, observacoes e melhorias", STYLE_TITLE))
        )
        r++
        val headerRow = r
        rows += SharedExcelRow(
            r++,
            listOf(
                SharedExcelCell(0, "Data/Hora", STYLE_HEADER),
                SharedExcelCell(1, "Usuario", STYLE_HEADER),
                SharedExcelCell(2, "Checklist", STYLE_HEADER),
                SharedExcelCell(3, "Obra", STYLE_HEADER),
                SharedExcelCell(4, "Tipo", STYLE_HEADER),
                SharedExcelCell(5, "Detalhe", STYLE_HEADER),
                SharedExcelCell(6, "ID", STYLE_HEADER)
            )
        )
        items.forEach { item ->
            val insp = item.inspecao
            val whenStr = formatDateTimeBr(insp.dataHorarioMsResumo().takeIf { it > 0 } ?: insp.criadoEm)
            val id = shortId(item)
            val head = listOf(whenStr, item.username, insp.formTipo.rotuloCurto, obraOf(insp))
            fun add(tipo: String, detalhe: String) {
                if (detalhe.isBlank()) return
                rows += SharedExcelRow(
                    r++,
                    cellsOf(head + listOf(tipo, detalhe, id))
                )
            }
            when (insp.formTipo) {
                FormTipo.INSPECAO_CAMPO -> {
                    val f = insp.form
                    add("Desvios", f.desviosEncontrados.textoExportacao())
                    if (f.desviosEncontrados.descricao.isNotBlank()) {
                        add("Desvios (descricao)", f.desviosEncontrados.descricao)
                    }
                    add("Acoes corretivas", f.acoesCorretivas.textoExportacao())
                    if (f.acoesCorretivas.descricao.isNotBlank()) {
                        add("Acoes corretivas (descricao)", f.acoesCorretivas.descricao)
                    }
                    add("Informacoes adicionais", f.informacoesAdicionais)
                    add("Conclusao", f.conclusao)
                    f.itensExtra.forEachIndexed { index, p ->
                        if (p.temConteudo()) {
                            add("Item extra", p.textoExportacao(index))
                        }
                    }
                    f.itens.filter {
                        it.resposta == RespostaTernaria.NAO || it.justificativa.isNotBlank()
                    }.forEach {
                        add(
                            "Item NC/justificativa",
                            "${it.titulo}: ${it.resposta?.rotulo.orEmpty()} - ${it.justificativa}"
                        )
                    }
                }
                FormTipo.RELATORIO_FOTO_ALOJAMENTO,
                FormTipo.RELATORIO_ALOJAMENTO -> {
                    val rel = insp.relatorioFoto
                    if (rel != null) {
                        add("Observacoes gerais", rel.observacoesGerais)
                        rel.itens.filter {
                            it.resposta == RespostaConformidade.NC || it.justificativa.isNotBlank()
                        }.forEach {
                            add(
                                "Item NC/justificativa",
                                "${it.tituloNumerado}: ${it.resposta?.rotuloSimNao.orEmpty()} - ${it.justificativa}"
                            )
                        }
                    }
                    val chk = insp.checklistAlojamento
                    if (chk != null) {
                        add("Liberado para uso", chk.liberadoParaUso?.rotulo.orEmpty())
                        chk.acoesCorrecao.forEach { a ->
                            add(
                                "Acao correcao",
                                "Item ${a.item} | Acao: ${a.acao} | Resp: ${a.responsavel} | Prazo: ${a.prazo}"
                            )
                        }
                        chk.itens.filter {
                            it.resposta == RespostaConformidade.NC || it.justificativa.isNotBlank()
                        }.forEach {
                            add(
                                "Checklist NC/justificativa",
                                "${it.tituloNumerado}: ${it.resposta?.codigo.orEmpty()} - ${it.justificativa}"
                            )
                        }
                    }
                }
                FormTipo.CHECKLIST_VETORES -> {
                    val chk = insp.checklistAlojamento ?: return@forEach
                    add("Numero de trabalhadores", chk.numeroTrabalhadores)
                    add("Data verificacao", chk.dataVerificacao)
                    chk.acoesCorrecao.forEach { a ->
                        add(
                            "Plano de acao",
                            "Item ${a.item} | Acao: ${a.acao} | Resp: ${a.responsavel} | Prazo: ${a.prazo}"
                        )
                    }
                    chk.itens.filter {
                        it.resposta == RespostaConformidade.NC || it.justificativa.isNotBlank()
                    }.forEach {
                        add(
                            "Item NC/justificativa",
                            "${it.tituloNumerado}: ${it.resposta?.codigo.orEmpty()} - ${it.justificativa}"
                        )
                    }
                }
                FormTipo.CHECKLIST_ALOJAMENTO -> {
                    val chk = insp.checklistAlojamento ?: return@forEach
                    add("Liberado para uso", chk.liberadoParaUso?.rotulo.orEmpty())
                    chk.acoesCorrecao.forEach { a ->
                        add(
                            "Acao correcao",
                            "Item ${a.item} | Acao: ${a.acao} | Resp: ${a.responsavel} | Prazo: ${a.prazo}"
                        )
                    }
                    chk.itens.filter {
                        it.resposta == RespostaConformidade.NC || it.justificativa.isNotBlank()
                    }.forEach {
                        add(
                            "Item NC/justificativa",
                            "${it.tituloNumerado}: ${it.resposta?.codigo.orEmpty()} - ${it.justificativa}"
                        )
                    }
                }
                FormTipo.CHECKLIST_DIARIO_HTMS,
                FormTipo.CHECKLIST_DIARIO_AXIA -> {
                    val cd = insp.checklistDiario ?: return@forEach
                    add("Observacoes gerais", cd.observacoesGerais)
                    cd.itens.filter {
                        it.resposta == RespostaConformidade.NC || it.justificativa.isNotBlank()
                    }.forEach {
                        add(
                            "Item NC/justificativa",
                            "${it.tituloNumerado}: ${it.resposta?.codigo.orEmpty()} - ${it.justificativa}"
                        )
                    }
                }
                FormTipo.COMUNICACAO_OCORRENCIAS -> {
                    val o = insp.comunicacaoOcorrencias ?: return@forEach
                    add("Acoes tomadas de imediato", o.acoesTomadasImediato)
                    add("Descricao do evento", o.descricaoEvento)
                    add("Tipo evento", o.tipoEvento?.rotulo.orEmpty())
                    add("Caracterizacao", o.caracterizacao?.rotulo.orEmpty())
                }
                FormTipo.REM_SEGURANCA_OBRAS,
            FormTipo.RELATORIO_SEGURANCA,
            FormTipo.ACOMPANHAMENTO_AMBIENTAL,
            FormTipo.EVIDENCIA_TREINAMENTO,
            FormTipo.ANALISE_VISUAL_NR -> Unit
            }
        }
        val lastDataRow = (r - 1).coerceAtLeast(headerRow)
        return SharedExcelSheet(
            name = "Acoes",
            rows = rows,
            merges = listOf("A1:G1"),
            freezeRows = headerRow,
            autoFilter = "A$headerRow:G$lastDataRow",
            tabColor = "FFB74D",
            colWidths = mapOf(0 to 16, 1 to 14, 2 to 14, 3 to 24, 4 to 22, 5 to 48, 6 to 12)
        )
    }

    private fun buildDashboard(items: List<ConsolidadoInspecao>, mapaResp: Map<String, String>): SharedExcelSheet {
        val resumoFirst = 4
        val resumoLast = (3 + items.size).coerceAtLeast(resumoFirst)

        val porObra = linkedMapOf<String, Int>()
        val porResp = linkedMapOf<String, Int>()
        val porTipo = linkedMapOf<String, Int>()
        val porDia = linkedMapOf<String, Int>()
        var totalC = 0
        var totalNc = 0
        var totalNa = 0
        var totalSem = 0
        items.forEach { item ->
            val insp = item.inspecao
            porTipo[insp.formTipo.rotulo] = (porTipo[insp.formTipo.rotulo] ?: 0) + 1
            val obra = obraOf(insp)
            porObra[obra] = (porObra[obra] ?: 0) + 1
            val resp = responsavelOf(item, mapaResp)
            porResp[resp] = (porResp[resp] ?: 0) + 1
            val dia = formatDateBr(insp.dataHorarioMsResumo().takeIf { it > 0 } ?: insp.criadoEm).take(10)
            porDia[dia] = (porDia[dia] ?: 0) + 1
            val counts = AgregacaoRespostas.of(insp)
            totalC += counts.c
            totalNc += counts.nc
            totalNa += counts.na
            totalSem += counts.sem
        }
        val obraList = porObra.entries.sortedByDescending { it.value }.take(12).toList()
        val respList = porResp.entries.sortedByDescending { it.value }.take(12).toList()

        fun excelQuote(s: String): String = "\"${s.replace("\"", "\"\"")}\""
        /** Conta/soma só linhas visíveis do Resumo (respeita AutoFiltro). */
        fun subtotalCount(first: Int, last: Int): String =
            "SUBTOTAL(3,Resumo!\$A\$$first:\$A\$$last)"
        fun subtotalSum(col: String, first: Int, last: Int): String =
            "SUBTOTAL(9,Resumo!\$$col\$$first:\$$col\$$last)"
        /** Conta critérios só em linhas visíveis (SUMPRODUCT + SUBTOTAL 103). */
        fun visibleMatch(col: String, first: Int, last: Int, criteria: String): String {
            val range = "Resumo!\$$col\$$first:\$$col\$$last"
            val anchor = "Resumo!\$$col\$$first"
            return "SUMPRODUCT(($range=$criteria)*(SUBTOTAL(103,OFFSET($anchor,ROW($range)-ROW($anchor),0))))"
        }

        val dashRows = mutableListOf<SharedExcelRow>()
        var dr = 1
        dashRows += SharedExcelRow(
            dr++,
            listOf(SharedExcelCell(0, "Dashboard - periodo consolidado", STYLE_TITLE)),
            height = 24
        )
        dashRows += SharedExcelRow(
            dr++,
            listOf(
                SharedExcelCell(
                    0,
                    "Use o filtro da aba Resumo — KPIs e graficos atualizam sozinho. Detalhes: Respostas e Acoes.",
                    STYLE_BODY
                )
            )
        )
        dr++ // 3 blank spacer conceptually — skip blank row by advancing
        // Row 4: KPI labels
        dashRows += SharedExcelRow(
            4,
            listOf(
                SharedExcelCell(0, "Inspecoes", STYLE_KPI_LABEL),
                SharedExcelCell(1, "Soma C", STYLE_KPI_LABEL),
                SharedExcelCell(2, "Soma NC", STYLE_KPI_LABEL),
                SharedExcelCell(3, "Soma NA", STYLE_KPI_LABEL),
                SharedExcelCell(4, "Crit. ALTA", STYLE_KPI_LABEL),
                SharedExcelCell(5, "Crit. MEDIA", STYLE_KPI_LABEL),
                SharedExcelCell(6, "Crit. BAIXA", STYLE_KPI_LABEL)
            )
        )
        dashRows += SharedExcelRow(
            5,
            listOf(
                SharedExcelCell(
                    0, items.size.toString(), STYLE_KPI_VALUE, numeric = true,
                    formula = subtotalCount(resumoFirst, resumoLast)
                ),
                SharedExcelCell(
                    1, totalC.toString(), STYLE_KPI_VALUE, numeric = true,
                    formula = subtotalSum("G", resumoFirst, resumoLast)
                ),
                SharedExcelCell(
                    2, totalNc.toString(), STYLE_KPI_VALUE, numeric = true,
                    formula = subtotalSum("H", resumoFirst, resumoLast)
                ),
                SharedExcelCell(
                    3, totalNa.toString(), STYLE_KPI_VALUE, numeric = true,
                    formula = subtotalSum("I", resumoFirst, resumoLast)
                ),
                SharedExcelCell(
                    4, items.count { AgregacaoRespostas.criticidade(AgregacaoRespostas.of(it.inspecao)) == "ALTA" }.toString(),
                    STYLE_CRIT_ALTA, numeric = true,
                    formula = visibleMatch("J", resumoFirst, resumoLast, "\"ALTA\"")
                ),
                SharedExcelCell(
                    5, items.count { AgregacaoRespostas.criticidade(AgregacaoRespostas.of(it.inspecao)) == "MEDIA" }.toString(),
                    STYLE_CRIT_MEDIA, numeric = true,
                    formula = visibleMatch("J", resumoFirst, resumoLast, "\"MEDIA\"")
                ),
                SharedExcelCell(
                    6, items.count { AgregacaoRespostas.criticidade(AgregacaoRespostas.of(it.inspecao)) == "BAIXA" }.toString(),
                    STYLE_CRIT_BAIXA, numeric = true,
                    formula = visibleMatch("J", resumoFirst, resumoLast, "\"BAIXA\"")
                )
            ),
            height = 22
        )

        // Series blocks starting row 7 (header) / 8 (data) — fixed for charts
        dashRows += SharedExcelRow(
            7,
            listOf(
                SharedExcelCell(0, "Distribuicao de respostas", STYLE_SECTION),
                SharedExcelCell(1, "Qtd", STYLE_SECTION),
                SharedExcelCell(3, "Inspecoes por obra", STYLE_SECTION),
                SharedExcelCell(4, "Qtd", STYLE_SECTION),
                SharedExcelCell(6, "Inspecoes por responsavel", STYLE_SECTION),
                SharedExcelCell(7, "Qtd", STYLE_SECTION)
            )
        )

        val respLabels = listOf("C / Sim", "NC / Nao", "NA / N/A", "Sem resposta")
        val respValues = listOf(totalC, totalNc, totalNa, totalSem)
        val respFormulas = listOf(
            subtotalSum("G", resumoFirst, resumoLast),
            subtotalSum("H", resumoFirst, resumoLast),
            subtotalSum("I", resumoFirst, resumoLast),
            null
        )
        val obraStart = 8
        val maxSeries = maxOf(4, obraList.size, respList.size, 1)
        for (i in 0 until maxSeries) {
            val cells = mutableListOf<SharedExcelCell>()
            if (i < respLabels.size) {
                cells += SharedExcelCell(0, respLabels[i], STYLE_BODY)
                cells += SharedExcelCell(
                    1,
                    respValues[i].toString(),
                    STYLE_BODY,
                    numeric = true,
                    formula = respFormulas[i]
                )
            }
            if (i < obraList.size) {
                val obra = obraList[i].key
                cells += SharedExcelCell(3, obra, STYLE_BODY)
                cells += SharedExcelCell(
                    4,
                    obraList[i].value.toString(),
                    STYLE_BODY,
                    numeric = true,
                    formula = visibleMatch("D", resumoFirst, resumoLast, excelQuote(obra))
                )
            }
            if (i < respList.size) {
                val quem = respList[i].key
                cells += SharedExcelCell(6, quem, STYLE_BODY)
                cells += SharedExcelCell(
                    7,
                    respList[i].value.toString(),
                    STYLE_BODY,
                    numeric = true,
                    formula = visibleMatch("E", resumoFirst, resumoLast, excelQuote(quem))
                )
            }
            dashRows += SharedExcelRow(obraStart + i, cells)
        }

        var next = obraStart + maxSeries + 1
        dashRows += SharedExcelRow(
            next++,
            listOf(SharedExcelCell(0, "Inspecoes por tipo (aba Resumo)", STYLE_SECTION))
        )
        dashRows += SharedExcelRow(
            next++,
            listOf(SharedExcelCell(0, "Tipo", STYLE_HEADER), SharedExcelCell(1, "Qtd", STYLE_HEADER))
        )
        porTipo.entries.sortedByDescending { it.value }.forEach { (k, v) ->
            dashRows += SharedExcelRow(
                next++,
                listOf(
                    SharedExcelCell(0, k, STYLE_BODY),
                    SharedExcelCell(
                        1, v.toString(), STYLE_BODY, numeric = true,
                        formula = visibleMatch("C", resumoFirst, resumoLast, excelQuote(k))
                    )
                )
            )
        }
        next++
        dashRows += SharedExcelRow(
            next++,
            listOf(SharedExcelCell(0, "Inspecoes por dia", STYLE_SECTION))
        )
        dashRows += SharedExcelRow(
            next++,
            listOf(SharedExcelCell(0, "Dia", STYLE_HEADER), SharedExcelCell(1, "Qtd", STYLE_HEADER))
        )
        porDia.entries.sortedBy { it.key }.forEach { (k, v) ->
            dashRows += SharedExcelRow(
                next++,
                listOf(
                    SharedExcelCell(0, k, STYLE_BODY),
                    SharedExcelCell(1, v.toString(), numeric = true, style = STYLE_BODY)
                )
            )
        }

        val charts = listOf(
            SharedExcelChart(
                type = SharedExcelChartType.PIE,
                title = "Distribuicao C / NC / NA",
                fromCol = 6,
                fromRow = 6,
                toCol = 12,
                toRow = 20,
                catRange = "\$A\$8:\$A\$11",
                valRange = "\$B\$8:\$B\$11",
                categories = respLabels,
                values = respValues.map { it.toDouble() }
            ),
            SharedExcelChart(
                type = SharedExcelChartType.BAR,
                title = "Inspecoes por obra",
                fromCol = 6,
                fromRow = 21,
                toCol = 12,
                toRow = 35,
                catRange = "\$D\$8:\$D\$${8 + obraList.size.coerceAtLeast(1) - 1}",
                valRange = "\$E\$8:\$E\$${8 + obraList.size.coerceAtLeast(1) - 1}",
                categories = if (obraList.isEmpty()) listOf("(sem obra)") else obraList.map { it.key },
                values = if (obraList.isEmpty()) listOf(0.0) else obraList.map { it.value.toDouble() }
            )
        )

        return SharedExcelSheet(
            name = "Dashboard",
            rows = dashRows,
            merges = listOf("A1:L1", "A2:L2"),
            freezeRows = 2,
            charts = charts,
            tabColor = "0D47A1",
            colWidths = mapOf(
                0 to 22, 1 to 12, 2 to 12, 3 to 28, 4 to 10, 5 to 12, 6 to 12
            )
        )
    }

    private fun critStyle(crit: String): Int = when (crit) {
        "ALTA" -> STYLE_CRIT_ALTA
        "MEDIA" -> STYLE_CRIT_MEDIA
        "BAIXA" -> STYLE_CRIT_BAIXA
        else -> STYLE_BODY
    }

    private fun shortId(item: ConsolidadoInspecao): String {
        val remote = item.remoteId?.trim().orEmpty()
        if (remote.isNotBlank()) {
            return if (remote.length <= 8) remote else remote.takeLast(8)
        }
        val local = item.inspecao.id.toString()
        return if (local.length <= 8) local else local.takeLast(8)
    }

    private fun efetivoOf(insp: InspecaoCompleta): String = when (insp.formTipo) {
        FormTipo.INSPECAO_CAMPO -> insp.form.efetivo
        FormTipo.RELATORIO_FOTO_ALOJAMENTO,
        FormTipo.RELATORIO_ALOJAMENTO ->
            insp.relatorioFoto?.quantidadeFuncionarios.orEmpty()
        else -> ""
    }

    private fun cellsOf(values: List<String>): List<SharedExcelCell> =
        values.mapIndexed { i, v -> SharedExcelCell(i, v) }
}
